import { useApolloClient } from '@apollo/client/react';
import { FormikConfig } from 'formik';
import { memo, useCallback, useMemo, useState } from 'react';
import * as yup from 'yup';
import { ButtonToggle } from '../../../components/ButtonToggle';
import { FormikEmailInput } from '../../../components/formik/FormEmailInput';
import { CreateModal } from '../../../components/Modal/CreateModal';
import { ToastType, useToastNotifications } from '../../../hooks/useToastNotifications';
import { useValidationTranslations } from '../../../hooks/useValidationTranslations';
import { T, useT } from '../../../translation/src';
import { UserRole, usePartnerCustomerInviteUsersMutation, useUserAddToOrganisationMutation } from '../../../types/graphqlTypes';

interface FormValues {
  emails: string[];
}

const ROLE_OPTIONS = [
  { value: UserRole.Admin, label: <T _str="admin" swc /> },
  { value: UserRole.Member, label: <T _str="member" swc /> },
];

export interface InviteUsersModalProps {
  onClose: () => void;
  open?: boolean;
  organisationId: string;
  organisationName: string;
  // Set for a reseller inviting into a customer billed to it, which it does not look into: the invitations go
  // through the partner (partnerCustomerInviteUsers), as admins only.
  partnerOrganisationId?: string;
}
// Invites people into an organisation the caller is not a member of: a superadmin from the admin center, a
// partner from its customer center. Admins or members; a guest needs projects picked, and stays with the
// organisation's own users page. The same address field as the share dialog.
const InviteUsersModal_: React.FC2<InviteUsersModalProps> = ({ onClose, open, organisationId, organisationName, partnerOrganisationId }) => {
  const apolloClient = useApolloClient();
  const [addUser, { loading: adding }] = useUserAddToOrganisationMutation();
  const [invitePartnerCustomer, { loading: invitingAsPartner }] = usePartnerCustomerInviteUsersMutation();
  const loading = adding || invitingAsPartner;
  const [role, setRole] = useState<UserRole>(UserRole.Admin);
  const { showToast } = useToastNotifications();
  const validation = useValidationTranslations();
  const addPeople = useT('add people by email', { swc: true });
  const sent = useT('invitations sent', { swc: true });
  const failed = useT('could not send the invitations', { swc: true });

  const validationSchema = useMemo(
    () => yup.object().shape({ emails: yup.array().of(yup.string().email(validation.validEmail)).min(1, validation.isRequired) }),
    [validation.isRequired, validation.validEmail],
  );

  const onSubmit: FormikConfig<FormValues>['onSubmit'] = useCallback(
    async ({ emails }, helpers) => {
      try {
        if (partnerOrganisationId) await invitePartnerCustomer({ variables: { organisationId: partnerOrganisationId, customerOrganisationId: organisationId, emails } });
        // One after the other: two invites racing would both try to create the same account.
        else for (const email of emails) await addUser({ variables: { organisationId, email, role } });
        // Whatever is on screen counts users: the organisation's card, its users page, the customer list.
        await apolloClient.refetchQueries({ include: 'active' });
        showToast({ title: sent, text: emails.join(', '), type: ToastType.SUCCESS });
        helpers.resetForm();
        onClose();
      } catch (e) {
        showToast({ title: failed, text: e instanceof Error ? e.message : String(e), type: ToastType.ERROR });
      }
    },
    [addUser, apolloClient, failed, invitePartnerCustomer, onClose, organisationId, partnerOrganisationId, role, sent, showToast],
  );

  const formik: FormikConfig<FormValues> = useMemo(() => ({ initialValues: { emails: [] }, onSubmit, validationSchema }), [onSubmit, validationSchema]);

  return (
    <CreateModal<FormValues> open={open} onClose={onClose} title={<T _str="invite people to {name}" args={{ name: organisationName }} swc />} createButtonTitle={<T _str="send invitation" swc />} formik={formik} isSubmitting={loading} noOverflow>
      {!partnerOrganisationId && <ButtonToggle options={ROLE_OPTIONS} onChange={setRole} value={role} />}
      <div className="mt-2 mb-3 text-sm text-gray-400">
        {role === UserRole.Admin || partnerOrganisationId ? <T _str="Admins have access to all projects and can manage the organisation, its users, its subscription and payments." /> : <T _str="Members have access to all projects." />}
      </div>
      <FormikEmailInput name="emails" placeholder={addPeople} aria-label={addPeople} />
    </CreateModal>
  );
};
export const InviteUsersModal = memo(InviteUsersModal_);
